import { useCallback, useEffect, useRef } from 'react';

interface PaginationTriggerOptions {
  onTopReached?: () => void;
  onBottomReached?: () => void;
  rootMargin?: string;
  threshold?: number;
  enabled?: boolean;
  cooldownMs?: number;
}

/**
 * Hook that uses Intersection Observer for efficient pagination triggering
 * More performant than scroll event listeners for pagination detection
 */
export const usePaginationTrigger = (
  containerRef: React.RefObject<HTMLElement | HTMLDivElement | null>,
  options: PaginationTriggerOptions = {}
) => {
  const {
    onTopReached,
    onBottomReached,
    rootMargin = '50px',
    threshold = 0.1,
    enabled = true,
    cooldownMs = 1000, // Default 1 second cooldown between triggers
  } = options;

  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const lastTopTriggerRef = useRef<number>(0);
  const lastBottomTriggerRef = useRef<number>(0);

  const handleIntersection = useCallback((entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;

      const now = Date.now();

      // Check if this is the top sentinel
      if (entry.target === topSentinelRef.current && onTopReached) {
        // Check if we're still in cooldown period
        if (now - lastTopTriggerRef.current < cooldownMs) {
          return;
        }
        lastTopTriggerRef.current = now;
        onTopReached();
      }
      
      // Check if this is the bottom sentinel
      if (entry.target === bottomSentinelRef.current && onBottomReached) {
        // Check if we're still in cooldown period
        if (now - lastBottomTriggerRef.current < cooldownMs) {
          return;
        }
        lastBottomTriggerRef.current = now;
        onBottomReached();
      }
    });
  }, [onTopReached, onBottomReached, cooldownMs]);

  useEffect(() => {
    if (!enabled || !containerRef.current) return;

    // Create intersection observer
    observerRef.current = new IntersectionObserver(handleIntersection, {
      root: containerRef.current,
      rootMargin,
      threshold,
    });

    const observer = observerRef.current;

    // Observe sentinels if they exist
    if (topSentinelRef.current) {
      observer.observe(topSentinelRef.current);
    }
    if (bottomSentinelRef.current) {
      observer.observe(bottomSentinelRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [enabled, containerRef, handleIntersection, rootMargin, threshold, cooldownMs]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return {
    topSentinelRef,
    bottomSentinelRef,
  };
};